import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLang } from "@/i18n";
import { Logo } from "@/components/Logo";

export const LangToggle = ({ dark = false }) => {
  const { lang, setLang } = useLang();
  return (
    <div
      className={`flex items-center rounded-full border text-[11px] font-jbmono uppercase tracking-wider overflow-hidden ${
        dark ? "border-white/20 text-white" : "border-[#353535]/20 text-[#353535]"
      }`}
    >
      {["es", "en"].map((l) => (
        <button
          key={l}
          data-testid={`lang-toggle-${l}`}
          onClick={() => setLang(l)}
          className={`px-3 py-1.5 transition-colors duration-300 ${
            lang === l
              ? dark
                ? "bg-[#f59c1a] text-[#353535]"
                : "bg-[#353535] text-white"
              : "opacity-60 hover:opacity-100"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

export const Navbar = ({ dark = false, accent = "#4361ee" }) => {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  const links = [
    { to: "/aws", label: t.nav.aws, id: "aws" },
    { to: "/martech", label: t.nav.martech, id: "martech" },
    { to: "/people-hr", label: t.nav.people, id: "people" },
    { to: "/nosotros", label: t.nav.nosotros, id: "nosotros" },
    { to: "/blog", label: t.nav.blog, id: "blog" },
  ];

  const textCls = dark ? "text-white" : "text-[#353535]";
  const mutedCls = dark ? "text-slate-400 hover:text-white" : "text-[#353535]/60 hover:text-[#353535]";

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 border-b backdrop-blur-xl ${
        dark ? "bg-[#0B1120]/70 border-white/10" : "bg-white/70 border-black/5"
      }`}
    >
      <div className="mx-auto flex max-w-[1400px] items-center justify-between px-5 sm:px-8 py-4">
        <Logo dark={dark} />
        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <Link
              key={l.id}
              to={l.to}
              data-testid={`nav-link-${l.id}`}
              className={`font-jakarta text-[13px] font-medium tracking-wide transition-colors duration-300 ${
                pathname.startsWith(l.to) ? textCls : mutedCls
              }`}
            >
              {l.label}
            </Link>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <LangToggle dark={dark} />
          <Link
            to="/contact"
            data-testid="nav-contact-cta"
            className="hidden sm:inline-flex items-center rounded-full px-5 py-2 text-[13px] font-jakarta font-semibold text-white transition-transform duration-300 hover:scale-[1.04]"
            style={{ backgroundColor: accent }}
          >
            {t.nav.contact}
          </Link>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(!open)}
            className={`lg:hidden ${textCls}`}
            aria-label="menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className={`lg:hidden overflow-hidden border-t ${dark ? "border-white/10 bg-[#0B1120]" : "border-black/5 bg-white"}`}
          >
            <div className="flex flex-col px-6 py-4 gap-4">
              {links.map((l) => (
                <Link
                  key={l.id}
                  to={l.to}
                  data-testid={`nav-mobile-link-${l.id}`}
                  onClick={() => setOpen(false)}
                  className={`font-jakarta text-base font-medium ${textCls}`}
                >
                  {l.label}
                </Link>
              ))}
              <Link
                to="/contact"
                data-testid="nav-mobile-contact-cta"
                onClick={() => setOpen(false)}
                className="font-jakarta text-base font-semibold"
                style={{ color: accent }}
              >
                {t.nav.contact}
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
